import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Phone, Sparkles, ShieldCheck, MapPin, Hammer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { site } from "@/lib/site";

const STORAGE_KEY = "hasSeenVisitPopup";
const DELAY_MS = 4000;

interface Props {
  ready: boolean;
}

const VisitPopup = ({ ready }: Props) => {
  const { openQuoteForm } = useQuoteForm();
  const [open, setOpen] = useState(false);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!ready) return;
    try {
      if (sessionStorage.getItem(STORAGE_KEY)) return;
    } catch {}
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, [ready]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {}
  };

  const handleCTA = () => {
    close();
    setTimeout(() => openQuoteForm(), 200);
  };

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="visit-popup-title"
      className="fixed inset-0 z-[9998] flex items-center justify-center p-4 sm:p-6 animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/55 backdrop-blur-md"
      />
      <div
        className="relative w-full max-w-[480px] max-h-[calc(100dvh-32px)] overflow-y-auto rounded-[28px] border border-white/10 bg-card shadow-2xl"
        style={{
          boxShadow:
            "0 30px 80px -20px hsl(var(--accent-dark) / 0.6), 0 0 0 1px hsl(var(--primary) / 0.15), 0 0 60px -10px hsl(var(--primary) / 0.35)",
          animation:
            "visit-pop-in 380ms cubic-bezier(0.33, 1, 0.68, 1) both",
        }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-32 rounded-t-[28px] opacity-70 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at top, hsl(var(--primary) / 0.25), transparent 70%)",
          }}
        />
        <button
          ref={closeBtnRef}
          onClick={close}
          aria-label="Close popup"
          className="absolute top-3 right-3 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted/80 hover:bg-muted text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="relative px-6 sm:px-8 pt-8 pb-6 sm:pt-10 sm:pb-8 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary mb-4">
            <Sparkles className="h-3.5 w-3.5" />
            Free Estimate
          </span>

          <h2
            id="visit-popup-title"
            className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-3"
          >
            Planning a Fence Project?
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground mb-6 leading-relaxed">
            Get a free, no-pressure estimate for fence installation, repair, or
            custom fencing across Dillon &amp; Summit County — plus refer a
            neighbor and you both earn a reward.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <Button
              onClick={handleCTA}
              size="lg"
              className="w-full font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Get Free Estimate
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="w-full font-semibold"
            >
              <a href={site.phoneHref}>
                <Phone className="h-4 w-4" />
                Call {site.phone}
              </a>
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-2 pt-4 border-t border-border/60">
            {[
              { icon: ShieldCheck, label: "Free Estimates" },
              { icon: MapPin, label: "Local Service" },
              { icon: Hammer, label: "Quality Work" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-1.5 text-[11px] sm:text-xs text-muted-foreground">
                <Icon className="h-4 w-4 text-primary" />
                <span className="font-medium">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes visit-pop-in {
          0% { opacity: 0; transform: translateY(12px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          [role="dialog"] > div { animation: none !important; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default VisitPopup;